// Public-readable landing page for a workspace guest-invite link
// (/accept-workspace-invite?token=...). Resolves the token via an
// unauthenticated endpoint so the invitee sees what they've been invited
// to BEFORE logging in; accepting requires auth — unauthenticated users
// are bounced to /login with a return URL so they land back here after
// signing in.
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Folder, Building2, Mail, ShieldCheck } from "lucide-react";

const ACCESS_LABEL = { R: "Read-only", W: "Write", A: "Admin" };

export default function AcceptWorkspaceInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const token = params.get("token");
    const [state, setState] = useState({ loading: true, invite: null, error: null });
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        if (!token) {
            setState({ loading: false, invite: null, error: "No invitation token provided." });
            return;
        }
        api.get(`/workspaces/invite/${encodeURIComponent(token)}`)
            .then((r) => setState({ loading: false, invite: r.data, error: null }))
            .catch((e) => setState({ loading: false, invite: null,
                error: e?.response?.data?.detail || "Invitation not found or expired." }));
    }, [token]);

    const accept = async () => {
        if (!user) {
            nav(`/login?next=${encodeURIComponent(`/accept-workspace-invite?token=${token}`)}`);
            return;
        }
        setAccepting(true);
        try {
            const r = await api.post(`/workspaces/invite/${encodeURIComponent(token)}/accept`);
            toast.success("Invitation accepted");
            nav(`/workspace?ws=${r.data.workspace_id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to accept");
        } finally { setAccepting(false); }
    };

    if (state.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground">Loading invitation…</div>
            </div>
        );
    }
    if (state.error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="accept-ws-invite-error">
                <div className="max-w-md w-full border border-border rounded-sm bg-card p-8 text-center">
                    <AlertCircle className="h-10 w-10 mx-auto text-destructive mb-4" strokeWidth={1.5} />
                    <div className="text-lg font-medium mb-2">Invitation unavailable</div>
                    <div className="text-sm text-muted-foreground mb-6">{state.error}</div>
                    <Button onClick={() => nav("/")} variant="outline" size="sm" data-testid="accept-ws-invite-home-link">
                        Go to Home
                    </Button>
                </div>
            </div>
        );
    }

    const { invite } = state;
    const emailMismatch = user && invite.invited_email &&
        user.email?.trim().toLowerCase() !== invite.invited_email?.trim().toLowerCase();

    return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12" data-testid="accept-ws-invite-page">
            <div className="max-w-lg w-full border border-border rounded-sm bg-card p-10">
                <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-sm mx-auto mb-5">
                    <Folder className="h-7 w-7 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-center mb-6">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">You're invited</div>
                    <h1 className="text-2xl font-semibold leading-tight">
                        Join the <span className="text-primary">{invite.workspace_name || "workspace"}</span> workspace
                    </h1>
                </div>
                <div className="space-y-2.5 mb-6 border border-border rounded-sm p-4 bg-secondary/20">
                    <Row icon={Mail} label="Invited email" value={invite.invited_email} testId="invite-email" />
                    <Row icon={Building2} label="From" value={`${invite.inviter_name || "Unknown"}${invite.source_org_name ? ` · ${invite.source_org_name}` : ""}`} testId="invite-from" />
                    <Row icon={ShieldCheck} label="Access" value={ACCESS_LABEL[invite.access] || invite.access} testId="invite-access" />
                </div>
                {emailMismatch && (
                    <div className="text-[12px] border border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-sm px-3 py-2 mb-4" data-testid="invite-email-mismatch">
                        This invitation was sent to <b>{invite.invited_email}</b> but you're signed in as <b>{user.email}</b>. Sign in with the invited address to accept.
                    </div>
                )}
                {invite.accepted && user && !emailMismatch && (
                    <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--success))] mb-4" data-testid="invite-already-accepted">
                        <CheckCircle2 className="h-4 w-4" /> You've already accepted this invitation.
                    </div>
                )}
                <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => nav("/")} data-testid="accept-ws-invite-decline">
                        Later
                    </Button>
                    <Button onClick={accept} disabled={accepting || emailMismatch} data-testid="accept-ws-invite-confirm">
                        {accepting ? "Accepting…" : user ? "Accept & open workspace" : "Sign in to accept"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function Row({ icon: Icon, label, value, testId }) {
    return (
        <div className="flex items-center gap-3 text-sm">
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
            <span className="eyebrow text-[10px] w-24 shrink-0">{label}</span>
            <span className="font-medium truncate" data-testid={testId}>{value || "—"}</span>
        </div>
    );
}
